// Iconografía del dominio de farmacovigilancia veterinaria.
//
// Criterio: no se reinventa lo genérico. Para campana, calendario, lupa o papelera se usa
// lucide-react, que ya es el lenguaje de iconos de la app. Aquí solo viven los conceptos
// propios del negocio que ninguna librería cubre.
//
// Retícula común con lucide, para que convivan sin desentonar:
//   lienzo 24×24 · trazo 1.8 (ajustable) · sin relleno · extremos redondeados · currentColor
//
// FUENTE DE VERDAD: los .svg de `Iconos/02_corregidos/`. Este archivo es su espejo, para que
// el entregable del diseñador y el código no se desincronicen.
//
// Dos reglas que salieron de revisar el set renderizado y que conviene no perder:
//
//  1. SE VALIDAN A 16 px, que es el tamaño real de uso. Varios dibujos que a 56 px lucían
//     perfectos, a 16 px eran una mancha. Si no se lee a 16, no sirve.
//  2. LOS DOCUMENTOS SE DISTINGUEN POR SILUETA, no por su marca interior. Seis hojas iguales
//     con un detalle distinto adentro son seis manchas idénticas a 16 px. Por eso aquí hay
//     portapapeles, hoja con esquina, hoja con sello que sobresale, hojas apiladas y sobre.

import type { SVGProps } from 'react';
import type { Icono } from './ui';

/**
 * La firma de TODOS los iconos de este archivo.
 *
 * Extiende las props nativas del `<svg>` para que quien lo use pueda pasar `className`,
 * `aria-label` o un manejador sin que haya que enumerarlos aqui uno a uno. `size` es
 * propio: es mas legible que escribir `width` y `height` iguales en cada llamada.
 */
export type PropsDeIcono = SVGProps<SVGSVGElement> & {
  size?: number | string;
};

const Ico = ({ size = 18, strokeWidth = 1.8, children, ...p }: PropsDeIcono) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" focusable="false" {...p}>
    {children}
  </svg>
);

/* ===========================================================================
   CATEGORÍAS DE EVENTO ADVERSO — las 7 casillas del Formato Verde.
   Se ven juntas en pantalla: cada una debe distinguirse de las otras seis.
   =========================================================================== */

// Huella: se usa la de lucide. Dibujé dos versiones propias y ninguna se leía mejor a 16 px.
import { PawPrint } from 'lucide-react';
export const EaAnimales = PawPrint;

// Persona + señal de alerta. NO lleva un signo "+": eso significaría "añadir persona".
// Es el icono más crítico del set: marca el caso con dato sensible de salud.
export const EaPersonas = (p: PropsDeIcono) => (
  <Ico {...p}>
    <circle cx="9.5" cy="7" r="3.1"/>
    <path d="M3.5 19.8c0-3.3 2.7-5.9 6-5.9.9 0 1.7.2 2.5.5"/>
    <path d="M17.3 13.3l3.5 6.1a.85.85 0 0 1-.74 1.28h-7a.85.85 0 0 1-.74-1.28l3.5-6.1a.85.85 0 0 1 1.48 0z"/>
    <path d="M17 16.2v1.5"/>
    <path d="M17 19.3h.01"/>
  </Ico>
);

// Cápsula + flecha descendente: el producto no rindió. Nada de relojes, la eficacia no es tiempo.
export const FaltaEficacia = (p: PropsDeIcono) => (
  <Ico {...p}>
    <rect x="2.6" y="3.8" width="12.6" height="6.4" rx="3.2"/>
    <path d="M8.9 3.8v6.4"/>
    <path d="M17.6 12.4v7.8"/>
    <path d="M14.2 16.8l3.4 3.4 3.4-3.4"/>
  </Ico>
);

// Reloj + gota: el tiempo que debe pasar antes de aprovechar leche o carne.
export const PeriodoRetiro = (p: PropsDeIcono) => (
  <Ico {...p}>
    <circle cx="9" cy="9.2" r="5.9"/>
    <path d="M9 6.1v3.1l2.1 1.5"/>
    <path d="M17.6 13.6c1.6 1.9 2.4 3.2 2.4 4.3a2.4 2.4 0 1 1-4.8 0c0-1.1.8-2.4 2.4-4.3z"/>
  </Ico>
);

// Microbio blindado: el agente resiste al antimicrobiano. Una píldora sola diría "medicamento".
export const Resistencia = (p: PropsDeIcono) => (
  <Ico {...p}>
    <path d="M12 2.6l7.6 2.7v6.4c0 4.6-3.2 7.9-7.6 9.3-4.4-1.4-7.6-4.7-7.6-9.3V5.3z"/>
    <circle cx="12" cy="10.8" r="2.5"/>
    <path d="M12 5.6v1.9"/><path d="M12 14.1v1.9"/>
    <path d="M6.9 10.8h1.9"/><path d="M15.2 10.8h1.9"/>
  </Ico>
);

// Hoja + gota: impacto ambiental. La gota la separa de la hoja genérica de "eco".
export const Medioambiental = (p: PropsDeIcono) => (
  <Ico {...p}>
    <path d="M3.8 19.2c0-7.4 4.7-11.2 12.2-11.2 0 7.4-4.7 11.2-12.2 11.2z"/>
    <path d="M3.8 19.2c2.3-3.8 4.7-5.7 7.5-7.1"/>
    <path d="M19 13.4c1.1 1.3 1.7 2.2 1.7 3a1.7 1.7 0 1 1-3.4 0c0-.8.6-1.7 1.7-3z"/>
  </Ico>
);

// Etiqueta tachada: uso distinto al del rótulo. Es una ETIQUETA, no un documento: como
// categoría de evento no debe confundirse con la familia de documentos.
export const OffLabel = (p: PropsDeIcono) => (
  <Ico {...p}>
    <path d="M3.4 11.7V5.2A1.7 1.7 0 0 1 5.1 3.5h6.6c.5 0 1 .2 1.3.6l7 7a1.9 1.9 0 0 1 0 2.7l-6.6 6.6a1.9 1.9 0 0 1-2.7 0l-7-7a1.9 1.9 0 0 1-.6-1.3z"/>
    <circle cx="7.4" cy="7.4" r="1.2"/>
    <path d="M11.2 11.2l4.4 4.4"/><path d="M15.6 11.2l-4.4 4.4"/>
  </Ico>
);

/* ===========================================================================
   DOCUMENTOS DEL PROCESO — cada uno con SILUETA propia.
   =========================================================================== */

// PORTAPAPELES con validación: es la captura interna, la que se llena en campo.
export const FormatoVerde = (p: PropsDeIcono) => (
  <Ico {...p}>
    <path d="M9 3.6H7.4A1.9 1.9 0 0 0 5.5 5.5v14A1.9 1.9 0 0 0 7.4 21.4h9.2a1.9 1.9 0 0 0 1.9-1.9v-14a1.9 1.9 0 0 0-1.9-1.9H15"/>
    <rect x="9" y="2" width="6" height="3.4" rx="1.2"/>
    <path d="M9.3 13.4l2.1 2.1 4.2-4.2"/>
  </Ico>
);

// Hoja con esquina + sello en asterisco: el formulario oficial de la autoridad.
export const FormatoAmarillo = (p: PropsDeIcono) => (
  <Ico {...p}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/>
    <path d="M14 3v5h5"/>
    <path d="M12 11.6v6.6"/>
    <path d="M9.1 13.3l5.8 3.2"/>
    <path d="M14.9 13.3l-5.8 3.2"/>
  </Ico>
);

// Hoja + barras: el análisis del Comité.
export const InformeFar = (p: PropsDeIcono) => (
  <Ico {...p}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/>
    <path d="M14 3v5h5"/>
    <path d="M9 17.6v-3.4"/><path d="M12 17.6v-5.6"/><path d="M15 17.6v-2.2"/>
  </Ico>
);

// Sobre + flecha de SALIDA. La carta se envía a la autoridad, no se recibe.
export const CartaAutoridad = (p: PropsDeIcono) => (
  <Ico {...p}>
    <path d="M21.5 11.4V6.6a2 2 0 0 0-2-2h-15a2 2 0 0 0-2 2v10.8a2 2 0 0 0 2 2H12"/>
    <path d="M2.5 7.4l8.4 5.4a2 2 0 0 0 2.2 0l8.4-5.4"/>
    <path d="M18 21.4v-6.6"/>
    <path d="M15.4 17.4L18 14.8l2.6 2.6"/>
  </Ico>
);

// El sello de recepción SOBRESALE de la hoja: rompe la silueta y no se confunde con los demás.
export const Cargo = (p: PropsDeIcono) => (
  <Ico {...p}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3.4"/>
    <path d="M14 3v5h5v3.4"/>
    <circle cx="17.2" cy="17.2" r="4.4"/>
    <path d="M15.3 17.2l1.4 1.4 2.5-2.6"/>
  </Ico>
);

// Hojas apiladas: el informe que se repite cada periodo.
export const Ips = (p: PropsDeIcono) => (
  <Ico {...p}>
    <path d="M8.5 5.6V4.2a2 2 0 0 1 2-2h4.7l4 4v8.4a2 2 0 0 1-2 2h-1.4"/>
    <rect x="3.8" y="7.2" width="10.9" height="14.2" rx="2"/>
    <path d="M6.8 11.8h5"/><path d="M6.8 15h5"/><path d="M6.8 18.2h3"/>
  </Ico>
);

/* ===========================================================================
   EVALUACIÓN Y PRODUCTO
   =========================================================================== */

// Árbol de decisión: el algoritmo de Naranjo. (Aprobado tal cual de la entrega del diseñador.)
export const Causalidad = (p: PropsDeIcono) => (
  <Ico {...p}>
    <rect width="6" height="4" x="9" y="3" rx="1"/>
    <rect width="5" height="4" x="3" y="17" rx="1"/>
    <rect width="5" height="4" x="16" y="17" rx="1"/>
    <path d="M12 7v5m0 0H5.5V17M12 12h6.5v5"/>
  </Ico>
);

// Frasco del producto veterinario. (Aprobado tal cual de la entrega del diseñador.)
export const Vial = (p: PropsDeIcono) => (
  <Ico {...p}>
    <path d="M9 3h6m-5 0v3a2 2 0 0 1-.6 1.4L7.6 9.2A3 3 0 0 0 6.8 11.3V19a2 2 0 0 0 2 2h6.4a2 2 0 0 0 2-2v-7.7a3 3 0 0 0-.8-2.1l-1.8-1.8A2 2 0 0 1 14 6V3"/>
    <line x1="6.8" y1="14" x2="17.2" y2="14"/>
    <path d="M10 10h4"/>
  </Ico>
);

// Pulso con un pico marcado. En contexto clínico se lee como severidad.
export const Gravedad = (p: PropsDeIcono) => (
  <Ico {...p}>
    <path d="M2.5 12.6h4l2.2-6.2 3.5 11.6 2.4-7.2 1.6 3.6h5.3"/>
  </Ico>
);

/* ===========================================================================
   Genéricos: se reexporta lucide. No tiene sentido redibujar una campana.
   =========================================================================== */
export {
  Inbox as Bandeja, FilePlus2 as NuevoCaso, Stethoscope as Comite, FileCheck2 as Aprobacion,
  Send as Notificar, ListChecks as Seguimiento, ChartLine as Indicadores, Settings as Configuracion,
  LayoutDashboard as Panel, Landmark as Autoridad, Package as Lote, Syringe as Jeringa,
  FolderOpen as Expediente, ShieldAlert as Sensible, Clock as Plazo, PenLine as Firma,
  Download as Exportar, Bell as Avisos, Users as Personas, Paperclip as Adjunto,
} from 'lucide-react';

// Alias con la nomenclatura de los archivos .svg, para que ambas convivan.
export {
  EaPersonas as EaSeguridadPersonas, FaltaEficacia as EaFaltaEficacia,
  PeriodoRetiro as EaPeriodoRetiro, Resistencia as EaResistencia,
  Medioambiental as EaMedioambiental, OffLabel as EaOffLabel,
  FormatoVerde as DocFormatoVerde, FormatoAmarillo as DocFormatoAmarillo,
  InformeFar as DocInformeFar03, CartaAutoridad as DocCartaAutoridad,
  Cargo as DocCargo, Ips as DocIps, Causalidad as EvalCausalidad,
  Vial as ProdVial, Gravedad as EstGravedad,
};

// Mapa de las 7 categorías del Formato Verde. Las claves son los CODIGOS de la tabla
// `categoria_ea` (BD `002_catalogos.sql`), que es de donde salen los `id` de
// CATALOGOS.categoriaEA. El prototipo los abreviaba (`seg_personas`, `falta_eficacia`)
// y cuatro de los siete iconos no resolvian contra el catalogo real.
export const ICONO_CATEGORIA: Record<string, Icono> = {
  seguridad_animal: EaAnimales,
  seguridad_en_personas: EaPersonas,
  falta_de_eficacia: FaltaEficacia,
  periodo_de_retiro: PeriodoRetiro,
  resistencia: Resistencia,
  medioambiental: Medioambiental,
  off_label: OffLabel,
};

// Mapa de los documentos del proceso.
export const ICONO_DOCUMENTO = {
  formato_verde: FormatoVerde,
  formato_amarillo: FormatoAmarillo,
  informe_far03: InformeFar,
  carta_autoridad: CartaAutoridad,
  cargo: Cargo,
  ips: Ips,
};
